import { CloudOff, Trash2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogMedia,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'

export interface LoseVoiceSampleDialogProps {
  open: boolean
  /**
   * `discard`: the operator closes the dialog, or records again, with a sample that is recorded
   * and not stored; `leave`: the operator is leaving the page while the dialog holds one (the
   * page's leave guard asks).
   */
  reason: 'discard' | 'leave'
  /** Keep the sample: stay. The default and the safe answer. */
  onKeep: () => void
  /** Lose it: discard it, or leave anyway. */
  onLose: () => void
}

const COPY = {
  discard: {
    Icon: Trash2,
    title: 'humint.voice.sample.lose.discard.title',
    body: 'humint.voice.sample.lose.discard.body',
    keep: 'humint.voice.sample.lose.discard.keep',
    lose: 'humint.voice.sample.lose.discard.lose',
  },
  leave: {
    Icon: CloudOff,
    title: 'humint.voice.sample.lose.leave.title',
    body: 'humint.voice.sample.lose.leave.body',
    keep: 'humint.subject.capture.leave.stay',
    lose: 'humint.subject.capture.leave.leave',
  },
} as const

/**
 * Asked before a voice sample is lost (ADR-0062): it lives only in this tab until the server
 * stored it — never at rest on the device (ADR-0048) — so closing the dialog, recording another
 * or leaving the page loses it for good.
 */
export function LoseVoiceSampleDialog({
  open,
  reason,
  onKeep,
  onLose,
}: LoseVoiceSampleDialogProps) {
  const { t } = useTranslation()
  const { Icon, title, body, keep, lose } = COPY[reason]
  return (
    <AlertDialog
      open={open}
      onOpenChange={next => {
        if (!next) onKeep()
      }}
    >
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogMedia>
            <Icon aria-hidden />
          </AlertDialogMedia>
          <AlertDialogTitle>{t(title)}</AlertDialogTitle>
          <AlertDialogDescription>{t(body)}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel className="h-12 sm:h-9" onClick={onKeep}>
            {t(keep)}
          </AlertDialogCancel>
          <AlertDialogAction variant="destructive" className="h-12 sm:h-9" onClick={onLose}>
            {t(lose)}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}
